import { useCallback } from 'react'
import { useHistory } from 'react-router'
import { useParams } from 'react-router-dom'
import { LocationState } from 'history'
import { StringifiableRecord, stringify } from 'query-string'
import { INavRoute } from 'types'

interface IUseRouteConfig {
  [key: string]: Omit<INavRoute, 'IconEl' | 'name'>
}

/**
 * Offers a centrally configured hook for accessing routes within an app or feature
 * @example
 * ```
 * const history = useRoutes({
 *   home: {
 *     name: 'Home',
 *     location: '/home'
 *   },
 *   settings: {
 *     name: 'Settings',
 *     location: '/settings'
 *   }
 * })
 *
 * // Then...
 * history.push('home', { isLoading: true })
 * ```
 */
export const useRoutes = <T extends IUseRouteConfig, K extends keyof T = keyof T>(routes: T) => {
  const history = useHistory()
  const currentParams = useParams()

  /**
   * Programmatically get the route we need
   */
  const getRouteByKey = useCallback(
    (routeKey: K) => {
      if (!routeKey || !routes[routeKey]) return undefined
      return routes[routeKey]
    },
    [routes]
  )

  /**
   * Allows the same logic for push/replace
   * @example const pushMethod = getHistoryMethod('push', routeKey, params, state)
   */
  const getHistoryMethod = useCallback(
    (
      methodName: keyof Pick<typeof history, 'push' | 'replace'>,
      routeKey: K,
      params: StringifiableRecord = {},
      state?: LocationState
    ) => {
      let { location = '' } = getRouteByKey(routeKey) || {}

      // compose and append params if there are any
      const allParams = { ...currentParams, ...params }
      if (Object.keys(allParams).length) {
        location += `?${stringify(allParams)}`
      }

      return history[methodName](location, state)
    },
    [currentParams, getRouteByKey, history]
  )

  /**
   * Replaces the current entry on the history stack
   * @example replace('home', { success: true }) // '/home?success=true'
   */
  const replaceWithParams = useCallback(
    (routeKey: K, params: StringifiableRecord = {}, state?: LocationState) => {
      return getHistoryMethod('replace', routeKey, params, state)
    },
    [getHistoryMethod]
  )

  /**
   * Pushes a new entry onto the history stack
   * @example push('home', { success: false }) // '/home?success=false'
   */
  const pushWithParams = useCallback(
    (routeKey: K, params: StringifiableRecord = {}, state?: LocationState) => {
      return getHistoryMethod('push', routeKey, params, state)
    },
    [getHistoryMethod]
  )

  return {
    ...history,
    getRouteByKey,
    pushWithParams,
    replaceWithParams,
  }
}
